import { Button, Container, Paper, Typography } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { ArrowBack, CheckCircle, Description, Download, Share } from "@mui/icons-material";
import { green } from "@mui/material/colors";

type Stat = { n: number | string; l: string };

interface RiskAssessmentCompletedCardProps {
    heading: string;
    subtitle: string;
    stats?: Stat[];
    onView?: () => void;
    primaryLabel?: string;
    onShare?: () => void;
    onDownload?: () => void;
    onBack: () => void;
    backLabel?: string;
}

const useStyles = makeStyles((theme) => ({
    paper: {
        maxWidth: 640,
        margin: "0 auto",
        padding: theme.spacing(5, 4),
        textAlign: "center",
    },
    checkCircle: {
        width: 64,
        height: 64,
        borderRadius: "50%",
        backgroundColor: theme.palette.mode === "dark" ? "rgba(118, 221, 120, 0.2)" : green[100],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto",
    },
    checkIcon: { color: green[600], fontSize: 40 },
    title: { fontFamily: ["Sailec", "Arial", "sans-serif"].join(","), fontWeight: 700, marginTop: theme.spacing(2) },
    subtitle: { color: theme.palette.text.secondary, margin: `${theme.spacing(1)} auto ${theme.spacing(4)}`, maxWidth: 460 },
    statRow: { display: "flex", gap: theme.spacing(2), justifyContent: "center", flexWrap: "wrap", marginBottom: theme.spacing(4) },
    statCard: { border: `1px solid ${theme.palette.divider}`, borderRadius: theme.spacing(1), padding: theme.spacing(2, 3), minWidth: 120 },
    statNumber: { fontWeight: 700 },
    statLabel: { color: theme.palette.text.secondary },
    actions: { display: "flex", gap: theme.spacing(2), justifyContent: "center", flexWrap: "wrap" },
    back: { marginTop: theme.spacing(3) },
}));

/**
 * Shared "risk assessment completed" confirmation card, used by both the template
 * completion flow and the upload-complete screen. Share reuses the proven
 * `view/:id?share=1` handoff (the parent builds the onShare navigation).
 */
export default function RiskAssessmentCompletedCard({
    heading,
    subtitle,
    stats,
    onView,
    primaryLabel = "View Assessment",
    onShare,
    onDownload,
    onBack,
    backLabel = "Back to Risk Assessments list",
}: RiskAssessmentCompletedCardProps) {
    const classes = useStyles();

    return (
        <Container>
            <Paper className={classes.paper}>
                <div className={classes.checkCircle}>
                    <CheckCircle className={classes.checkIcon} />
                </div>
                <Typography variant="h5" className={classes.title}>
                    {heading}
                </Typography>
                <Typography variant="body1" className={classes.subtitle}>
                    {subtitle}
                </Typography>

                {stats && stats.length > 0 && (
                    <div className={classes.statRow}>
                        {stats.map((s) => (
                            <div className={classes.statCard} key={s.l}>
                                <Typography variant="h4" className={classes.statNumber}>
                                    {s.n}
                                </Typography>
                                <Typography variant="caption" className={classes.statLabel}>
                                    {s.l}
                                </Typography>
                            </div>
                        ))}
                    </div>
                )}

                <div className={classes.actions}>
                    {onView && (
                        <Button variant="contained" color="primary" startIcon={<Description />} onClick={onView}>
                            {primaryLabel}
                        </Button>
                    )}
                    {onShare && (
                        <Button variant="outlined" color="primary" startIcon={<Share />} onClick={onShare}>
                            Share with Others
                        </Button>
                    )}
                    {onDownload && (
                        <Button variant="outlined" color="primary" startIcon={<Download />} onClick={onDownload}>
                            Download
                        </Button>
                    )}
                </div>

                <Button className={classes.back} color="primary" startIcon={<ArrowBack />} onClick={onBack}>
                    {backLabel}
                </Button>
            </Paper>
        </Container>
    );
}
